var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { change, destroy, getFormValues } from 'redux-form';
import { apiDeleteClientConsultationDraft, apiUpdateClientConsultationDraft, apiGetOneClientConsultationDraft, } from '@/actions/ClientMyPageActions/index';
import { RECRUITMENT_TYPE_CAREFULLY } from '@/define/consts/consultation';
import { useNavigate } from '@/hooks/lib/react-router/useNavigate';
import { wrapDispatch } from '@/utils/actions/dispatch';
import { clientMyPageConsultationPath, clientMyPageConsultationConfirmPath, } from '@/utils/router/links';
import Layout from '../../../Layout';
import AuthenticationHoc from '../../../System/AuthenticationHoc';
import { FaqSearch } from '../MyPageCommon/Faq/FaqSearch';
import Navigation from '../MyPageCommon/Navigation/Navigation';
import ConsultationForm, { formName } from './Form/ConsultationForm';
const draftConfirmText = '自動バックアップされた変更内容があります。\r\nOKを選択すると内容が復元され、キャンセルを選択するとバックアップを削除します。\r\n\r\n復元しますか？';
/**
 * TODO: TS化、tailwind化
 */
const Consultations = () => {
    const history = useNavigate();
    const dispatch = useDispatch();
    const { clientRfpCount, formValues } = useSelector((state) => ({
        clientRfpCount: state.clientRfpCount,
        formValues: getFormValues(formName)(state),
    }));
    const rfpUnreadCount = clientRfpCount.rfp_unread_count;
    const { initialValues, month, year } = useMemo(() => {
        const now = new Date();
        const y = now.getFullYear();
        const m = now.getMonth() + 1;
        return {
            initialValues: {
                budgetType: '2',
                considerationEndMonth: m.toString(),
                considerationEndYear: y.toString(),
                considerationType: '1',
                recruitmentType: RECRUITMENT_TYPE_CAREFULLY.toString(),
            },
            month: m,
            year: y,
        };
    }, []);
    const ref = useRef({
        init: initialValues,
        values: formValues,
    });
    /**
     * 入力完了
     */
    const onSubmit = useCallback(() => {
        history.push(clientMyPageConsultationConfirmPath());
    }, [history]);
    /**
     * 下書きデータ保存
     * @param values 下書き保存する値
     */
    const saveDraft = useCallback((values) => __awaiter(void 0, void 0, void 0, function* () {
        yield wrapDispatch(dispatch, apiUpdateClientConsultationDraft({ json: values }, {}));
    }), [dispatch]);
    /**
     * 下書きデータ 復元
     * @param draftData 取得した下書きデータ
     */
    const recoverDraft = useCallback((formDispatch, draft, init) => {
        var _a, _b, _c, _d;
        const draftFormValues = {
            aboutMeetingTool: draft.about_meeting_tool || '',
            additionalInformation: draft.additional_information || '',
            budget: ((_a = draft.budget) === null || _a === void 0 ? void 0 : _a.toString()) || '',
            budgetType: ((_b = draft.budget_type) === null || _b === void 0 ? void 0 : _b.toString()) || init.budgetType,
            considerationEndMonth: draft.consideration_end_at
                ? (new Date(draft.consideration_end_at).getMonth() + 1).toString()
                : init.considerationEndMonth,
            considerationEndYear: draft.consideration_end_at
                ? new Date(draft.consideration_end_at).getFullYear().toString()
                : init.considerationEndYear,
            considerationType: ((_c = draft.consideration_type) === null || _c === void 0 ? void 0 : _c.toString()) || init.considerationType,
            contactableTime1: draft.contactable_time_1 || '',
            contactableTime2: draft.contactable_time_2 || '',
            contactableTime3: draft.contactable_time_3 || '',
            recruitmentType: ((_d = draft.recruitment_type) === null || _d === void 0 ? void 0 : _d.toString()) || init.recruitmentType,
            whatRequest: draft.what_request || '',
        };
        Object.keys(draftFormValues).forEach((filedName) => {
            formDispatch(change(formName, filedName, draftFormValues[filedName]));
        });
    }, []);
    /**
     * 下書きデータ 削除
     */
    const removeDraft = useCallback((formDispatch) => __awaiter(void 0, void 0, void 0, function* () {
        yield wrapDispatch(formDispatch, apiDeleteClientConsultationDraft({}, {}));
    }), []);
    // 下書きデータ取得 api実行
    useEffect(() => {
        const { init, values } = ref.current;
        (() => __awaiter(void 0, void 0, void 0, function* () {
            if (values) {
                // 確認画面から戻った場合 下書き取得しない
                return;
            }
            const { payload: { consultation: draft }, } = yield wrapDispatch(dispatch, apiGetOneClientConsultationDraft({}, {}));
            if (!draft) {
                // 下書きの値がない場合 処理終了
                return;
            }
            // eslint-disable-next-line no-alert
            const confirmResult = window.confirm(draftConfirmText);
            if (confirmResult) {
                // OK の場合
                recoverDraft(dispatch, draft, init);
            }
            else {
                // キャンセルの場合
                removeDraft(dispatch);
            }
        }))();
        return () => {
            if (window.location.pathname !== clientMyPageConsultationConfirmPath() &&
                window.location.pathname !== clientMyPageConsultationPath()) {
                dispatch(destroy(formName));
            }
        };
    }, [dispatch, ref, recoverDraft, removeDraft]);
    return (_jsxs(Layout, Object.assign({ mainClassName: 'l-main client_mypage no-need-bottom-space' }, { children: [_jsxs("div", Object.assign({ className: 'mypage_container' }, { children: [_jsx(Navigation, { activeTab: 'consultations', rfpUnreadCount: rfpUnreadCount }), _jsxs("div", Object.assign({ className: 'mypage_card' }, { children: [_jsx("div", Object.assign({ className: 'card_header' }, { children: _jsxs("h3", Object.assign({ className: 'label' }, { children: ["\u30103\u5206\u3067\u5165\u529B\u5B8C\u4E86\u3011", _jsx("wbr", {}), "\u30D5\u30A9\u30FC\u30E0\u306B\u8A18\u5165\u3057\u3066\u3001", _jsx("wbr", {}), "Proffit\u3068\u9032\u3081\u65B9\u3092\u76F8\u8AC7\u3059\u308B"] })) })), _jsx("div", Object.assign({ className: 'card_body' }, { children: _jsx(ConsultationForm, { currentMonth: month, currentYear: year, initialValues: initialValues, onSubmit: onSubmit, saveDraft: saveDraft }) }))] }))] })), _jsx(FaqSearch, {})] })));
};
export default AuthenticationHoc({ isClient: true })(Consultations);
